import { useEffect, useRef, useState } from "react"
import type { KeyboardEvent, MouseEvent } from "react"
import { createPortal } from "react-dom"
import { Select } from "antd"
import type { SelectProps } from "antd"
import { GripIcon } from "components/ui/icons/grip/GripIcon"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { moveItem, resolveDropGap } from "helpers/modelNumberOrder.helper"
import style from "./OrderedModelNumbersSelect.module.scss"

const {
	wrapper,
	draggingRoot,
	tag,
	tagSource,
	handle,
	grip,
	order,
	tagLabel,
	remove,
	dropBar,
	ghost,
	option,
	optionOrder,
} = style

// Pointer travel before a press on a tag becomes a drag, so a plain click never reorders.
const DRAG_THRESHOLD_PX = 4

type TOption = { value: number; label: string }

type Props = {
	value: number[]
	onChange: (value: number[]) => void
	options: TOption[]
	placeholder?: string
	"data-testid"?: string
}

type TDrag = { value: number; label: string; offsetX: number; offsetY: number; x: number; y: number }
type TDropBar = { left: number; top: number; height: number }

// Multi-select whose selection ORDER is the value: picks append, tags are numbered and can be
// dragged (or moved with Alt + arrow keys on the grip) to reorder. Processing picks the first match.
export const OrderedModelNumbersSelect = ({
	value,
	onChange,
	options,
	placeholder = "Select model numbers",
	"data-testid": testId,
}: Props) => {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [drag, setDrag] = useState<TDrag | null>(null)
	const [isOpen, setIsOpen] = useState(false)
	// Set for the duration of a press on a tag, so the open/close toggle that press triggers is ignored.
	const tagPressRef = useRef(false)
	const [dropBarPos, setDropBarPos] = useState<TDropBar | null>(null)

	// The gesture outlives renders, so it reads the latest props through a ref.
	const latest = useRef({ value, onChange })
	useEffect(() => {
		latest.current = { value, onChange }
	}, [value, onChange])

	// Delegated native `pointerdown` for the drag; the window listeners outlive the tag's render.
	useEffect(() => {
		const root = wrapperRef.current
		if (!root) return
		let cleanupGesture: (() => void) | null = null

		const getTagRects = () =>
			Array.from(root.querySelectorAll<HTMLElement>("[data-order-tag]")).map((el) => el.getBoundingClientRect())

		const onPointerDown = (event: PointerEvent) => {
			const target = event.target as HTMLElement
			const tagEl = target.closest<HTMLElement>("[data-order-tag]")
			// One gesture at a time: a second pointer would orphan the first one's window listeners.
			if (cleanupGesture || event.button !== 0 || !tagEl || target.closest("[data-order-remove]")) return
			const pointerId = event.pointerId
			const tagValue = Number(tagEl.dataset.orderTag)
			const label = tagEl.dataset.orderLabel ?? ""
			const rect = tagEl.getBoundingClientRect()
			const startX = event.clientX
			const startY = event.clientY
			let started = false
			let gap: number | null = null

			const onMove = (e: PointerEvent) => {
				if (e.pointerId !== pointerId) return
				if (!started) {
					if (Math.hypot(e.clientX - startX, e.clientY - startY) < DRAG_THRESHOLD_PX) return
					started = true
					document.documentElement.classList.add(draggingRoot)
				}
				setDrag({
					value: tagValue,
					label,
					offsetX: startX - rect.left,
					offsetY: startY - rect.top,
					x: e.clientX,
					y: e.clientY,
				})
				const drop = resolveDropGap(getTagRects(), e.clientX, e.clientY)
				const box = root.getBoundingClientRect()
				if (!drop) return
				gap = drop.index
				setDropBarPos({ left: drop.x - box.left, top: drop.top - box.top, height: drop.height })
			}

			const finish = (commit: boolean) => {
				cleanupGesture?.()
				if (!commit || !started || gap === null) return
				const { value: current, onChange: emit } = latest.current
				const next = moveItem(current, current.indexOf(tagValue), gap)
				if (next !== current) emit(next)
			}
			const onUp = (e: PointerEvent) => e.pointerId === pointerId && finish(true)
			const onCancel = (e: PointerEvent) => e.pointerId === pointerId && finish(false)
			const onKey = (e: globalThis.KeyboardEvent) => {
				if (e.key !== "Escape" || !started) return
				// Escape abandons the drag only; it must not also close the surrounding modal.
				e.preventDefault()
				e.stopPropagation()
				finish(false)
			}

			cleanupGesture = () => {
				window.removeEventListener("pointermove", onMove)
				window.removeEventListener("pointerup", onUp)
				window.removeEventListener("pointercancel", onCancel)
				window.removeEventListener("keydown", onKey, true)
				document.documentElement.classList.remove(draggingRoot)
				cleanupGesture = null
				setDrag(null)
				setDropBarPos(null)
			}
			window.addEventListener("pointermove", onMove)
			window.addEventListener("pointerup", onUp)
			window.addEventListener("pointercancel", onCancel)
			window.addEventListener("keydown", onKey, true)
		}

		root.addEventListener("pointerdown", onPointerDown)
		return () => {
			root.removeEventListener("pointerdown", onPointerDown)
			cleanupGesture?.()
		}
	}, [])

	const onHandleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tagValue: number) => {
		if (!event.altKey || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return
		// Keep antd's selector from treating the arrows as its own navigation.
		event.preventDefault()
		event.stopPropagation()
		const from = value.indexOf(tagValue)
		const next = moveItem(value, from, event.key === "ArrowLeft" ? from - 1 : from + 2)
		if (next === value) return
		onChange(next)
		// The tag remounts at its new index; follow it so repeated presses keep moving the same one.
		requestAnimationFrame(() =>
			wrapperRef.current?.querySelector<HTMLElement>(`[data-order-handle="${tagValue}"]`)?.focus(),
		)
	}

	// A press on a tag is for the tag, not the dropdown. Not stopPropagation: in React 18 that stops
	// the native event too, and other selects' outside-click listener on window would never close.
	const onTagMouseDown = (event: MouseEvent) => {
		event.preventDefault()
		// rc-select stops this press reaching window, so another select's outside-click never fires:
		// blur its input instead, which closes its dropdown the same way.
		const active = document.activeElement
		if (active instanceof HTMLElement && !wrapperRef.current?.contains(active)) active.blur()
		tagPressRef.current = true
		setTimeout(() => (tagPressRef.current = false))
	}

	const onOpenChange = (open: boolean) => {
		if (!tagPressRef.current) setIsOpen(open)
	}

	// rc-select's own key handler sits on its root: Enter would cancel the ✕ click and open the
	// dropdown, Backspace would delete the LAST tag. Keys on a tag act on that tag only.
	const onTagKeyDown = (event: KeyboardEvent<HTMLElement>, onClose: (() => void) | undefined) => {
		// Tab moves on, Escape still closes the dropdown / the modal around it.
		if (event.key === "Tab" || event.key === "Escape") return
		event.stopPropagation()
		if (event.key === "Backspace" || event.key === "Delete") {
			event.preventDefault()
			onClose?.()
			// The focused tag is gone; land on the search input rather than <body>.
			wrapperRef.current?.querySelector<HTMLInputElement>("input")?.focus()
		}
	}

	const tagRender: SelectProps["tagRender"] = ({ value: tagValue, label, onClose }) => {
		const id = tagValue as number
		const index = value.indexOf(id)
		const text = typeof label === "string" ? label : String(label ?? id)
		return (
			<span
				className={`${tag} ${drag?.value === id ? tagSource : ""}`}
				data-order-tag={id}
				data-order-label={text}
				data-testid="ordered-model-number-tag"
				role="presentation"
				onMouseDown={onTagMouseDown}
				onKeyDown={(e) => onTagKeyDown(e, onClose)}
			>
				<button
					type="button"
					className={handle}
					data-order-handle={id}
					// Its click would reach rc-select's selector, which moves focus to the search input.
					onClick={(e) => e.stopPropagation()}
					aria-label={`Move ${text}, position ${index + 1} of ${value.length}. Alt + arrow keys to move.`}
					onKeyDown={(e) => onHandleKeyDown(e, id)}
				>
					<GripIcon className={grip} />
				</button>
				<span className={order}>{index + 1}</span>
				<span className={tagLabel}>{text}</span>
				<button type="button" className={remove} data-order-remove aria-label={`Remove ${text}`} onClick={onClose}>
					<CloseIcon size={8} color="currentColor" />
				</button>
			</span>
		)
	}

	const optionRender: SelectProps["optionRender"] = (opt) => {
		const index = value.indexOf(opt.value as number)
		return (
			<span className={option}>
				<span>{opt.label}</span>
				{index >= 0 && <span className={optionOrder}>{index + 1}</span>}
			</span>
		)
	}

	return (
		<div className={wrapper} ref={wrapperRef}>
			<Select<number[]>
				open={isOpen}
				onOpenChange={onOpenChange}
				mode="multiple"
				showSearch
				placeholder={placeholder}
				style={{ width: "100%" }}
				value={value}
				onChange={onChange}
				options={options}
				optionFilterProp="label"
				tagRender={tagRender}
				optionRender={optionRender}
				data-testid={testId}
			/>
			{dropBarPos && (
				<span
					className={dropBar}
					style={{ left: dropBarPos.left, top: dropBarPos.top, height: dropBarPos.height }}
					data-testid="ordered-model-number-drop-bar"
				/>
			)}
			{drag &&
				createPortal(
					<span className={`${tag} ${ghost}`} style={{ left: drag.x - drag.offsetX, top: drag.y - drag.offsetY }}>
						<GripIcon className={grip} />
						<span className={order}>{value.indexOf(drag.value) + 1}</span>
						<span className={tagLabel}>{drag.label}</span>
						<span className={remove}>
							<CloseIcon size={8} color="currentColor" />
						</span>
					</span>,
					document.body,
				)}
		</div>
	)
}
